/* eslint-disable max-len */
import React, { FC, useMemo, useState } from 'react'
import Tips from '../common/Tips'
import { secToHourMinute } from '../../../utils/date'

interface GraphItem {
  label: string
  color: string
  value: number
}

interface Props {
  title: string
  graphItems: GraphItem[]
  legendSquare?: boolean
}

const LabelingBar: FC<Props> = (props) => {
  const {
    title,
    graphItems,
    legendSquare,
  } = props

  const DISPLAY_LIMIT_NUM = 5

  const [hoverName, setHoverName] = useState<string>("")

  const totalTime = useMemo(() => {
    if (!graphItems) return 0
    return graphItems.reduce((s, w) => s + w.value, 0)
  }, [graphItems])

  const onMouseOverGraph = (name: string) => {
    setHoverName(name)
  }

  const onMouseLeaveGraph = () => {
    setHoverName("")
  }

  return (
    <>
      <div
        style={{
          position: "relative",
          border: "solid 1px #999999",
          borderRadius: "4px",
          padding: "8px",
        }}
      >
        {
          !!title && (
            <div
              style={{
                position: "absolute",
                top: "0",
                left: "4px",
                transform: "translateY(-50%)",
                backgroundColor: "#FFFFFF",
                fontSize: "10px",
                color: "#666666",
                padding: "0 4px",
              }}
            >
              {title}
            </div>
          )
        }
        {
          !(graphItems.length === 1 && !graphItems[0].value) && (
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "10px",
                justifyContent: "center",
                padding: "0 8px 8px",
                flexWrap: "wrap",
              }}
            >
              {
                graphItems.map((v) => (
                  <div
                    key={`legend_${v.label}_${v.color}`}
                    onMouseOver={() => onMouseOverGraph(v.label)}
                    onFocus={() => { /* */ }}
                    onMouseLeave={() => onMouseLeaveGraph()}
                    role="none"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "2px",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: v.color,
                        width: "12px",
                        height: "12px",
                        borderRadius: legendSquare ? "0" : "50%",
                      }}
                    />
                    {v.label}
                  </div>
                ))
              }
            </div>
          )
        }
        <div
          style={{
            width: "100%",
            display: "flex",
          }}
        >
          {
            graphItems.length === 1 && !graphItems[0].value ? (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "62px",
                    color: "#999999",
                    fontSize: "16px",
                  }}
                >
                  データがありません
                </div>
              </>
            ) : (
              <>
                {
                  graphItems.map((v) => (
                    <React.Fragment key={`${v.label}_${v.value}`}>
                      <div
                        style={{
                          width: `${(v.value / totalTime) * 100}%`,
                          height: "44px",
                          background: `linear-gradient(to right, ${v.color} 0 calc(100% - 1px), #FFFFFF calc(100% - 1px) 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "default",
                          position: "relative",
                          opacity: (!hoverName || hoverName === v.label) ? "1" : "0.4",
                        }}
                        onMouseOver={() => onMouseOverGraph(v.label)}
                        onFocus={() => { /* */ }}
                        onMouseLeave={() => onMouseLeaveGraph()}
                      >
                        <Tips
                          description={`${v.label}%br%${secToHourMinute(v.value)} (${Math.floor((v.value / totalTime) * 1000) / 10}%)`}
                          className="inner_graph"
                          icon={false}
                        >
                          <div
                            style={{
                              color: v.color,
                              filter: "invert(100%) grayscale(100%) contrast(100)",
                              fontSize: "16px",
                              textAlign: "center",
                              overflow: "hidden",
                            }}
                          >
                            {
                              (v.value / totalTime) * 100 > DISPLAY_LIMIT_NUM && (
                                <>
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {v.label}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "12px",
                                    }}
                                  >
                                    {secToHourMinute(v.value)}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "10px",
                                    }}
                                  >
                                    {`(${Math.floor((v.value / totalTime) * 1000) / 10}%)`}
                                  </div>
                                </>
                              )
                            }
                          </div>
                        </Tips>
                      </div>
                    </React.Fragment>
                  ))
                }
              </>
            )
          }
        </div>
      </div>
    </>
  )
}

LabelingBar.defaultProps = {
  legendSquare: false,
}

export default LabelingBar
